import { Document as ApiDocument, Folder as ApiFolder } from '@quadminds/client-apis/agents';
import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { buildDocumentParams } from '../components/pages/Knowledge/helpers/useGetDocuments.helper';
import { Folder, Paginated } from '../interfaces/paginated.interface';
import { documentToFile, documentToFolder } from '../mappers/file/file';
import ChatService from '../services/chat.service';
import { FileItem } from '../types/File/File.types';

const serializeFilters = (filters?: Record<string, string>): string => {
    if (!filters) {
        return 'root';
    }

    const sortedEntries = Object.entries(filters)
        .map(([key, value]) => [key, String(value)] as [string, string])
        .sort(([a], [b]) => a.localeCompare(b));

    return new URLSearchParams(sortedEntries).toString();
};

export const useGetDocuments = (organizationId: string, filters?: Record<string, string>, enabled = true) => {
    const chatService = new ChatService();

    const serializedFilters = useMemo(() => serializeFilters(filters), [filters]);

    return useInfiniteQuery({
        queryKey: ['documents', organizationId, serializedFilters, 'v3'],
        queryFn: ({ pageParam }) => {
            if (!organizationId) {
                throw new Error('Organization ID is required');
            }

            const params = buildDocumentParams(filters, organizationId, pageParam);
            return chatService.getKnowledgeDocuments(organizationId, params);
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const { page, total_pages } = lastPage.pagination;
            return page < total_pages ? page + 1 : undefined;
        },
        select: (response) => {
            return {
                ...response,
                pages: response.pages.map((page) => ({
                    ...page,
                    data: [
                        ...page.data.map((doc) => documentToFile(doc as ApiDocument)),
                        ...page.folders.map((folder) => documentToFolder(folder as ApiFolder)),
                    ],
                })),
            } as InfiniteData<Paginated<FileItem, Folder>>;
        },
        enabled: enabled && !!organizationId,
        staleTime: 5 * 60 * 1000,
        refetchOnMount: 'always',
        refetchOnWindowFocus: false,
        gcTime: 10 * 60 * 1000,
    });
};

export default useGetDocuments;
